import { useCallback, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useAdmin } from '../AdminContext'
import { templateByKey } from './marketingOptions'
import { designFileName, downloadNode } from './marketingRules'
import type { MarketingDesign } from '../../core/types/marketing'

type Options = {
  /** Bestandsnaam zonder merkvoorvoegsel; standaard de naam van het ontwerp. */
  name?: string
  successTitle?: string
  /** Tip als het mislukt (meestal een foto die niet laadt). */
  errorHint?: string
}

/**
 * Een ontwerp als PNG op ware grootte downloaden — gedeeld door de ontwerp-editor en de social planner.
 * Zet `canvas` op het volle-grootte-ontwerp; `busy` staat aan zolang het bestand gemaakt wordt.
 */
export function useDesignDownload() {
  const { site } = useAdmin()
  const canvas = useRef<HTMLDivElement>(null)
  const [busy, setBusy] = useState(false)

  const download = useCallback(
    async (design: MarketingDesign, { name, successTitle = 'Gedownload', errorHint }: Options = {}) => {
      if (!canvas.current || busy) return
      const t = templateByKey(design.template)
      setBusy(true)
      try {
        await downloadNode(canvas.current, designFileName(name || design.name, site.salon.name), t.exportScale)
        toast.success(successTitle, { description: `${t.w * t.exportScale} × ${t.h * t.exportScale} px, PNG` })
      } catch {
        toast.error('Downloaden lukte niet', {
          description: errorHint ?? 'Waarschijnlijk laadt een foto niet. Kies een andere foto of probeer het zo nog eens.',
        })
      } finally {
        setBusy(false)
      }
    },
    [busy, site.salon.name],
  )

  return { canvas, busy, download }
}
